import { toNumericId } from '@/helpers';
import { ApiService } from '@/support/ApiService';
import type { Animal } from '@/model/Animal.model';
import type { FC } from 'react';
import React, { useState } from 'react';

interface RemoveFromGroupProps {
  subject: Animal | Array<Animal>;
  handleCallback: (text?: string) => void;
  closeModal: () => void;
}

/**
 * Confirmation modal for removing one or more animals from their study group.
 */
const RemoveFromGroup: FC<RemoveFromGroupProps> = ({ subject, handleCallback, closeModal }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const subjects = Array.isArray(subject) ? subject : [subject];
  const isMultiple = subjects.length > 1;
  const what = isMultiple ? 'these animals' : 'this animal';
  const where = isMultiple ? 'their groups' : 'its group';

  const removeFromGroup = async (event: React.MouseEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const animals = subjects.map((s) => ({
        id: toNumericId(s.id),
        study_group_id: null,
      }));

      await ApiService.call({
        endpoint: 'PATCH /api/v1/animals',
        body: { animals },
        options: { onError: { throw: true, toast: false, slug: 'remove-from-group-bulk-update' } },
      });

      handleCallback('Successfully removed from group');
    } catch {
      setError('Unable to remove from group. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <form className={`ui__narrow-modal ${isLoading && 'disabled-form'}`}>
      <div className="pb3 tc">
        <h3 className="normal f4 pb2">
          Are you sure you want to remove {what} from {where}?
        </h3>
      </div>
      {error && <p className="red">{error}</p>}
      <div>
        <button type="submit" onClick={removeFromGroup} className="button w-100">
          Remove from group
        </button>
        <button type="button" className="button plain db w-100 mt2" onClick={closeModal}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default RemoveFromGroup;
